/**
 * Skeleton component for role detail page loading state
 * @module components/common/RoleDetailSkeleton
 */

import React from 'react'
import { Card } from '@/components/ui/Card'
import { SkeletonText, SkeletonRounded, SkeletonCircular } from './Skeleton'

/**
 * Role detail loading skeleton
 *
 * Displays skeleton placeholders matching the structure of the role detail page
 */
export const RoleDetailSkeleton: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <SkeletonRounded width={80} height={36} />
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <SkeletonText width={200} height={36} />
              <SkeletonRounded width={70} height={24} />
            </div>
            <SkeletonText width={300} height={16} />
          </div>
        </div>
        <SkeletonRounded width={100} height={36} />
      </div>

      {/* Stats Cards */}
      <div className="dashboard-grid lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <SkeletonText width={100} height={14} />
                <SkeletonText width={80} height={28} />
              </div>
              <SkeletonCircular width={48} height={48} />
            </div>
          </Card>
        ))}
      </div>

      {/* Two column grid - Responsibilities & Requirements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Array.from({ length: 2 }).map((_, cardIndex) => (
          <Card key={cardIndex} className="p-6">
            <div className="flex items-center gap-2 mb-6">
              <SkeletonCircular width={20} height={20} />
              <SkeletonText width={150} height={24} />
            </div>
            <div className="space-y-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="flex items-start gap-3">
                  <SkeletonCircular width={8} height={8} />
                  <SkeletonText width={`${70 + Math.random() * 20}%`} height={16} />
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      {/* Salary Details */}
      <Card className="p-6">
        <SkeletonText width={180} height={24} className="mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <SkeletonText width={100} height={14} />
              <SkeletonText width={120} height={28} />
            </div>
          ))}
        </div>
      </Card>

      {/* Employees list */}
      <Card className="p-6">
        <SkeletonText width={220} height={24} className="mb-6" />
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-brand-dark/50 rounded-monumenta">
              <SkeletonCircular width={40} height={40} />
              <div className="space-y-1">
                <SkeletonText width={150} height={16} />
                <SkeletonText width={200} height={14} />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}
